import React, { useMemo } from 'react';

import { InpockInput, Paragraph1, Paragraph2, Select, SmallText } from 'inpock-ui-react';

import { InterestFreeCardsType, MARKET_STATUS, PaymentMethod } from '@/apis';
import {
  CARD_OPTIONS,
  INSTALLMENT_OPTIONS,
  PAYMENT_METHOD,
  REFUND_BANK_OPTIONS,
} from '@/constants';
import NaverPayIcon from '@/assets/icons/icon-naver-pay.svg';
import KakaoPayIcon from '@/assets/icons/icon-kakao-pay.svg';
import TossPayIcon from '@/assets/icons/icon-toss-pay.svg';
import { Icon, Spinner } from '@/components';
import { openPopup } from '@/utils';
import { useForm } from '@/hooks';

import { CardInfoType, FormProps, RefundInfoType } from '../types';

import * as S from './styled';

interface PaymentMethodProps extends FormProps<PaymentMethod> {
  refundInfo: RefundInfoType;
  setRefundInfo: React.Dispatch<React.SetStateAction<RefundInfoType>>;
  cardInfo: CardInfoType;
  setCardInfo: React.Dispatch<React.SetStateAction<CardInfoType>>;
  totalCost: number;
  interestFreeCards: InterestFreeCardsType[];
  marketStatus: MARKET_STATUS | null;
  is_v2: boolean;
  loading: boolean;
}

const PAYMENT_OPTIONS = [
  { method: PAYMENT_METHOD.CARD, label: '신용·체크카드' },
  { method: PAYMENT_METHOD.NAVER_PAY, label: '네이버페이', icon: NaverPayIcon },
  { method: PAYMENT_METHOD.KAKAO_PAY, label: '카카오페이', icon: KakaoPayIcon },
  { method: PAYMENT_METHOD.TOSS_PAY, label: '토스페이', icon: TossPayIcon },
  { method: PAYMENT_METHOD.VIRTUAL_ACCOUNT, label: '가상계좌' },
];

export function PaymentMethodSection(props: PaymentMethodProps) {
  const {
    form,
    setForm,
    refundInfo,
    setRefundInfo,
    cardInfo,
    setCardInfo,
    totalCost,
    interestFreeCards,
    marketStatus,
    is_v2,
    loading,
  } = props;
  const { onInput } = useForm({ ...refundInfo, ...cardInfo });

  const handleRefundInfoChange = (field: keyof RefundInfoType, value: string) => {
    onInput(value, field);
    setRefundInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleCardInfoChange = (field: keyof CardInfoType, value: string) => {
    onInput(value, field);
    setCardInfo((prev) => ({ ...prev, [field]: value }));
  };

  const selectedCardInterestFree = useMemo(() => {
    if (!cardInfo.cardCompany) return null;

    const selectedCard = CARD_OPTIONS.find((card) => card.value === cardInfo.cardCompany);
    return interestFreeCards.find((card) => card.company_code === selectedCard?.code);
  }, [cardInfo.cardCompany, interestFreeCards]);

  const modifiedInstallmentOptions = useMemo(() => {
    if (!selectedCardInterestFree) return INSTALLMENT_OPTIONS;
    return INSTALLMENT_OPTIONS.map((option) => ({
      ...option,
      label: selectedCardInterestFree.installment_free_months.includes(Number(option.value))
        ? `${option.label} (무이자)`
        : option.label,
    }));
  }, [selectedCardInterestFree]);

  // 총 결제 금액이 최소 결제 금액을 넘는지 확인
  const totalCostExceedsMinimumAmount = useMemo(
    () => selectedCardInterestFree && totalCost >= selectedCardInterestFree.minimum_payment_amount,
    [selectedCardInterestFree, totalCost],
  );

  // 할부 옵션을 보여줄지 확인 : 카드사 선택, 총 결제 금액이 최소 결제 금액 이상, 버전이 v2인 경우 마켓 상태가 ACTIVE일 때
  const showInstallmentOptions =
    cardInfo.cardCompany &&
    (totalCostExceedsMinimumAmount || totalCost >= 50000) &&
    (is_v2 ? marketStatus === MARKET_STATUS.ACTIVE : true);

  const onClickInquiry = () => {
    openPopup('https://inpocklink.channel.io/home', 375, 812);
  };

  const paymentOptionByMarketStatus = () => {
    if (!is_v2) return PAYMENT_OPTIONS;
    switch (marketStatus) {
      case MARKET_STATUS.UNDER_REVIEW:
        return [{ method: PAYMENT_METHOD.CARD, label: '신용·체크카드' }];
      case MARKET_STATUS.UNDER_CARD_REVIEW:
        return [
          { method: PAYMENT_METHOD.CARD, label: '신용·체크카드' },
          { method: PAYMENT_METHOD.VIRTUAL_ACCOUNT, label: '가상계좌' },
        ];
      case MARKET_STATUS.ACTIVE:
        return PAYMENT_OPTIONS;
      default:
        return [];
    }
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <section className="d-flex mt-24 justify-content-between">
        <Paragraph1 fontWeight="bold">결제 수단</Paragraph1>
        <div onClick={onClickInquiry}>
          <S.CheckoutInquiry className="text-underline text-orange">
            결제에 문제가 있으신가요?
          </S.CheckoutInquiry>
        </div>
      </section>
      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ height: '120px' }}
        >
          <Spinner />
        </div>
      ) : (
        <S.SectionBody>
          <section className="mt-16 mb-24">
            <S.Buttons>
              {paymentOptionByMarketStatus().map((option) => (
                <div className="p-grid-col" key={option.method}>
                  <S.Button checked={form === option.method} onClick={() => setForm(option.method)}>
                    <span>{option.icon ? <S.PayTypeIcon src={option.icon} /> : option.label}</span>
                  </S.Button>
                </div>
              ))}
            </S.Buttons>
          </section>

          <S.Inputs>
            {form === 'CARD' && (
              <React.Fragment>
                <Select
                  defaultOption="카드사 선택"
                  options={CARD_OPTIONS}
                  value={cardInfo.cardCompany}
                  onInput={(input_value) => handleCardInfoChange('cardCompany', input_value)}
                  style={{ minWidth: '130px' }}
                  rules={{ required: true }}
                  width="100%"
                />
                {showInstallmentOptions && (
                  <Select
                    options={modifiedInstallmentOptions}
                    value={String(cardInfo.cardInstallmentPlan)}
                    onInput={(input_value) =>
                      handleCardInfoChange('cardInstallmentPlan', input_value)
                    }
                    style={{ minWidth: '130px' }}
                    rules={{ required: true }}
                    width="100%"
                  />
                )}
              </React.Fragment>
            )}

            {form === 'VIRTUAL_ACCOUNT' && (
              <React.Fragment>
                <SmallText style={{ color: 'rgb(240, 68, 82)' }}>
                  * 주문 후 1시간 안에 입금하지 않으면 주문이 취소됩니다.
                </SmallText>
                <Select
                  options={REFUND_BANK_OPTIONS}
                  value={refundInfo.refund_bank_code}
                  defaultOption="은행 선택"
                  onInput={(input_value) => handleRefundInfoChange('refund_bank_code', input_value)}
                  guide="환불 계좌 정보"
                  required
                  style={{ minWidth: '130px' }}
                  width="100%"
                  rules={{ required: true }}
                />
                <InpockInput
                  value={refundInfo.refund_account_holder}
                  placeholder="예금주 입력"
                  onInput={(input_value) =>
                    handleRefundInfoChange('refund_account_holder', input_value)
                  }
                  style={{ minWidth: '130px' }}
                  maxLength={30}
                  width="100%"
                  validator={(_rules, value) => {}}
                  rules={{ required: true }}
                />
                <InpockInput
                  value={refundInfo.refund_account}
                  placeholder="계좌번호 입력('-' 제외)"
                  onInput={(input_value) => handleRefundInfoChange('refund_account', input_value)}
                  style={{ minWidth: '130px' }}
                  maxLength={30}
                  width="100%"
                  validator={(_rules, value) => {}}
                  rules={{ required: true, no_white_space: true, just_number: true }}
                />
                <SmallText color="gray600">
                  주문상품 취소 및 반품 시 해당 계좌로 입금해 드려요.
                </SmallText>
              </React.Fragment>
            )}
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
              onClick={() =>
                openPopup('https://pgweb.tosspayments.com/tosspayments/MainPopUp.do', 720, 812)
              }
            >
              <Paragraph2 color="gray600">신용카드 무이자 할부 안내</Paragraph2>
              <Icon icon="ArrowRight" size={12} color="rgb(112, 112, 112)" />
            </div>
          </S.Inputs>
        </S.SectionBody>
      )}
    </div>
  );
}
